/**
 * SimpleAhaProductView — the Simple product view with "Aha moment" psychology.
 *
 * Plan 1897, Features 5 & 6. Wraps the generic SimpleTableView and layers on:
 *  - summary stats (with clickable "needs images" → server-side filter)
 *  - motivational empty state (0 products) / filtered empty state
 *  - "almost ready" nudge when nothing is on the storefront yet
 *  - a calm "store is live" milestone marker + one-time "catalog growing" toast
 *
 * It is a real component (not inline render-prop JSX) so hooks are legal.
 */

import { useMemo, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useFilterStore } from "@/lib/store/filterStore";
import type { FilterDescriptor } from "@/lib/erp/filterTypes";
import type { ItemWithVariants } from "@/lib/erp";
import {
  SimpleTableView,
  type SimpleTableColumn,
  type SimpleTableStat,
} from "@/features/common/components/SimpleTableView";
import {
  MotivationalEmptyState,
  FilteredEmptyState,
  AlmostReadyNudge,
  StoreReadyIndicator,
} from "./AhaMomentBanners";

const EMPTY_FILTERS: FilterDescriptor[] = [];
const CATALOG_MILESTONE_KEY = "aha_catalog_5_celebrated";
const IMAGE_FILTER: FilterDescriptor = { field: "image", operator: "isNotSet", value: null };

function isEnabled(item: ItemWithVariants): boolean {
  const v = item.storefront_enabled as unknown;
  return v === 1 || v === true || v === "1";
}
function hasImage(item: ItemWithVariants): boolean {
  return !!(item as Record<string, unknown>).primary_image || !!item.image;
}
function hasPrice(item: ItemWithVariants): boolean {
  return !!item.standard_rate && Number(item.standard_rate) > 0;
}

type Props = {
  items: ItemWithVariants[];
  isLoading: boolean;
  total: number;
  language: string;
  columns: SimpleTableColumn<ItemWithVariants>[];
  filterStoreKey: string;
  subRowLabel: string;
  onRowClick: (id: string, label?: string) => void;
  onAddProduct: () => void;
};

export function SimpleAhaProductView({
  items,
  isLoading,
  total,
  language,
  columns,
  filterStoreKey,
  subRowLabel,
  onRowClick,
  onAddProduct,
}: Props) {
  const scopedFilters = useFilterStore(
    (s) => s.scopedFilters[filterStoreKey] ?? EMPTY_FILTERS,
  );

  const imageFilterActive = useMemo(
    () => scopedFilters.some((f) => f.field === "image" && f.operator === "isNotSet"),
    [scopedFilters],
  );
  const hasActiveFilters = scopedFilters.length > 0;

  // Derive catalog health from the loaded parent items.
  const { totalProducts, onStorefront, needsImage, storeReady } = useMemo(() => {
    const parents = items.filter((item) => !item.variant_of);
    return {
      totalProducts: parents.length,
      onStorefront: parents.filter(isEnabled).length,
      needsImage: parents.filter((p) => !hasImage(p)).length,
      storeReady: parents.some((p) => isEnabled(p) && hasImage(p) && hasPrice(p)),
    };
  }, [items]);

  const toggleImageFilter = () => {
    const store = useFilterStore.getState();
    const current = store.scopedFilters[filterStoreKey] ?? [];
    if (imageFilterActive) {
      store.setFiltersForKey(
        filterStoreKey,
        current.filter((f) => !(f.field === "image" && f.operator === "isNotSet")),
      );
    } else {
      store.setFiltersForKey(filterStoreKey, [...current, IMAGE_FILTER]);
    }
  };

  const clearFilters = () => {
    useFilterStore.getState().setFiltersForKey(filterStoreKey, []);
  };

  const summaryStats = useMemo<SimpleTableStat[]>(() => {
    const stats: SimpleTableStat[] = [
      { label: language === "th" ? "สินค้า" : "products", value: totalProducts },
      {
        label: language === "th" ? "เผยแพร่แล้ว" : "on storefront",
        value: onStorefront,
        color: onStorefront > 0 ? "green" : "default",
      },
    ];
    if (needsImage > 0 || imageFilterActive) {
      stats.push({
        label: language === "th" ? "ยังไม่มีรูปภาพ" : "need images",
        value: needsImage,
        color: "orange",
        onClick: toggleImageFilter,
        active: imageFilterActive,
      });
    }
    return stats;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, totalProducts, onStorefront, needsImage, imageFilterActive]);

  // Milestone: one-time gentle toast when the catalog reaches 5 products.
  const milestoneFired = useRef(false);
  useEffect(() => {
    if (milestoneFired.current || hasActiveFilters) return;
    const count = total || totalProducts;
    if (count < 5) return;
    if (localStorage.getItem(CATALOG_MILESTONE_KEY)) {
      milestoneFired.current = true;
      return;
    }
    milestoneFired.current = true;
    localStorage.setItem(CATALOG_MILESTONE_KEY, new Date().toISOString());
    toast.success(
      language === "th" ? "แคตตาล็อกของคุณกำลังเติบโต!" : "Your catalog is growing!",
      {
        description:
          language === "th"
            ? `ตอนนี้คุณมีสินค้า ${count} รายการแล้ว`
            : `You now have ${count} products.`,
        duration: 5000,
      },
    );
  }, [total, totalProducts, hasActiveFilters, language]);

  // Nudge / milestone marker above the table.
  const topBanner = useMemo(() => {
    if (storeReady) return <StoreReadyIndicator language={language} />;
    if (totalProducts > 0 && onStorefront === 0 && !hasActiveFilters) {
      return <AlmostReadyNudge language={language} />;
    }
    return null;
  }, [storeReady, totalProducts, onStorefront, hasActiveFilters, language]);

  // Empty state: distinguish "no products at all" from "filter hid everything".
  const emptyState = useMemo(() => {
    if (isLoading) return undefined;
    if (hasActiveFilters) {
      return <FilteredEmptyState language={language} onClear={clearFilters} />;
    }
    return <MotivationalEmptyState language={language} onAddProduct={onAddProduct} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasActiveFilters, language, onAddProduct]);

  return (
    <SimpleTableView<ItemWithVariants>
      items={items}
      columns={columns}
      isLoading={isLoading}
      total={total}
      getRowId={(row) => row.name}
      onRowClick={onRowClick}
      thumbnail={{
        imageFields: ["primary_image", "image"],
        labelFields: ["item_name", "item_code", "name"],
        size: 48,
      }}
      getSubRows={(row) => (row as ItemWithVariants).variants}
      subRowLabel={subRowLabel}
      summaryStats={summaryStats}
      topBanner={topBanner}
      emptyState={emptyState}
    />
  );
}
